import ClearIcon from "@mui/icons-material/Clear";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useChangePassword,
  useRemoveOauth2Client,
  useUpdateProfile,
  useUser,
} from "model";
import { useEffect, useState } from "react";

import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, analytics } from "common/analytics";
import AgriwebbLogoIcon from "common/icons/AgriwebbLogoIcon";
import { useTranslation } from "common/locales";
import {
  normalizeLanguage,
  supportedLanguages,
} from "common/locales/languages";
import { AreaUnit, normalizeAreaUnit } from "common/utils/area";
import {
  ExternalProvider,
  externalProviderName,
} from "features/signup/externalProviders";
import { useIntl } from "react-intl";
import { logout } from "services/auth";
import { useAppStore } from "stores";

interface ProfileDialogProps {
  open: boolean;
  onClose: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormErrors {
  name?: string;
  mail?: string;
  confirmPassword?: string;
}

export default function ProfileDialog({ open, onClose }: ProfileDialogProps) {
  const user = useAppStore((state) => state.user);
  const setUser = useAppStore((state) => state.setUser);
  const setLocale = useAppStore((state) => state.setLocale);
  const { formatMessage: f } = useIntl();
  const { t } = useTranslation();
  const { notify } = useNotification();

  // Profile fields
  const [name, setName] = useState("");
  const [mail, setMail] = useState("");
  const [tradingName, setTradingName] = useState("");
  const [lang, setLang] = useState("en-GB");
  const [areaUnit, setAreaUnit] = useState<AreaUnit>("HECTARES");

  // Password change
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [errors, setErrors] = useState<FormErrors>({});

  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();
  const removeOauth2ClientMutation = useRemoveOauth2Client();

  // Refetch user data after oauth2 removal
  const { refetch: refetchUser } = useUser(user?.guid, open);

  // Reset the form to the current user whenever the dialog opens or the
  // underlying user changes (e.g. after a successful save).
  useEffect(() => {
    if (!user) return;
    setName(user.name ?? "");
    setMail(user.mail ?? "");
    setTradingName(user.tradingName ?? "");
    setLang(normalizeLanguage(user.lang));
    setAreaUnit(normalizeAreaUnit(user.areaUnit));
    setShowPasswordFields(false);
    setNewPassword("");
    setConfirmPassword("");
    setErrors({});
  }, [user, open]);

  if (!user) {
    return <></>;
  }

  const hasAgriwebb =
    !!user.oauth2clients &&
    user.oauth2clients.includes(ExternalProvider.AGRIWEBB);

  const profileDirty =
    name !== (user.name ?? "") ||
    mail !== (user.mail ?? "") ||
    tradingName !== (user.tradingName ?? "") ||
    lang !== normalizeLanguage(user.lang) ||
    areaUnit !== normalizeAreaUnit(user.areaUnit);

  const validateProfile = () => {
    const nextErrors: FormErrors = {};
    if (!name.trim()) {
      nextErrors.name = t("settings.name.required");
    }
    if (!EMAIL_REGEX.test(mail.trim())) {
      nextErrors.mail = t("settings.mail.invalid");
    }
    setErrors((prev) => ({
      ...prev,
      name: nextErrors.name,
      mail: nextErrors.mail,
    }));
    return !nextErrors.name && !nextErrors.mail;
  };

  const handleSaveProfile = async () => {
    if (!validateProfile()) return;

    const langChanged = lang !== normalizeLanguage(user.lang);

    try {
      const updated = await updateProfileMutation.mutateAsync({
        userId: user.guid,
        updates: {
          name: name.trim(),
          mail: mail.trim(),
          tradingName: tradingName.trim() || null,
          lang,
          areaUnit,
        },
      });

      // Keep the global store in sync so the rest of the app sees the changes.
      setUser({
        ...user,
        name: updated.name,
        mail: updated.mail,
        tradingName: updated.tradingName,
        lang: updated.lang,
        areaUnit: updated.areaUnit,
      });
      if (langChanged) {
        setLocale(lang);
      }

      analytics.track(AnalyticsEvents.PROFILE_UPDATED, {
        languageChanged: langChanged,
      });
      notify(t("settings.update.success"));
    } catch {
      notify(t("settings.update.error"));
    }
  };

  const handleSavePassword = async () => {
    if (!newPassword) return;
    if (newPassword !== confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: t("settings.password.mismatch"),
      }));
      return;
    }

    await changePasswordMutation.mutateAsync({
      userId: user.guid,
      newPassword,
    });
    notify(t("passwordChange.success"));
    analytics.track(AnalyticsEvents.PASSWORD_CHANGED);

    setNewPassword("");
    setConfirmPassword("");
    setShowPasswordFields(false);
    setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
  };

  const cancelPasswordChange = () => {
    setShowPasswordFields(false);
    setNewPassword("");
    setConfirmPassword("");
    setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
  };

  const removeIntegration = async (externalProvider: ExternalProvider) => {
    await removeOauth2ClientMutation.mutateAsync({
      userId: user.guid,
      externalProvider,
    });
    await refetchUser();
    analytics.track(AnalyticsEvents.INTEGRATION_REMOVED, {
      provider: externalProviderName[externalProvider],
    });
    notify(
      t("oauth2ClientRemove.success", {
        providerName: externalProviderName[externalProvider],
      })
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="profile-dialog-title"
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id="profile-dialog-title">
        {f({ id: "settings.title" })}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField
            label={f({ id: "settings.name" })}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            size="small"
            inputProps={{ "data-private": true }}
          />
          <TextField
            label={f({ id: "settings.mail" })}
            value={mail}
            onChange={(e) => setMail(e.target.value)}
            error={!!errors.mail}
            helperText={errors.mail}
            type="email"
            fullWidth
            size="small"
            inputProps={{ "data-private": true }}
          />
          <TextField
            label={f({ id: "settings.tradingName" })}
            value={tradingName}
            onChange={(e) => setTradingName(e.target.value)}
            fullWidth
            size="small"
            inputProps={{ "data-private": true }}
          />
          <TextField
            select
            label={f({ id: "settings.language" })}
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            fullWidth
            size="small"
          >
            {supportedLanguages.map((language) => (
              <MenuItem key={language.value} value={language.value}>
                {language.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={f({ id: "settings.areaUnit" })}
            value={areaUnit}
            onChange={(e) => setAreaUnit(e.target.value as AreaUnit)}
            fullWidth
            size="small"
          >
            <MenuItem value="HECTARES">
              {f({ id: "settings.areaUnit.hectares" })}
            </MenuItem>
            <MenuItem value="ACRES">
              {f({ id: "settings.areaUnit.acres" })}
            </MenuItem>
          </TextField>

          {!showPasswordFields ? (
            <Box sx={{ mt: -1 }}>
              <Button
                variant="text"
                color="secondary"
                size="small"
                onClick={() => setShowPasswordFields(true)}
                sx={{ px: 0.5 }}
              >
                {f({ id: "settings.changePassword" })}
              </Button>
            </Box>
          ) : (
            <Stack
              spacing={1.5}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <SectionLabel>
                {f({ id: "settings.changePassword" })}
              </SectionLabel>
              <TextField
                label={f({ id: "settings.newPassword" })}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                type="password"
                fullWidth
                size="small"
                autoComplete="new-password"
                helperText={f({ id: "settings.password.hint" })}
              />
              <TextField
                label={f({ id: "settings.confirmPassword" })}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setErrors((prev) => ({
                    ...prev,
                    confirmPassword: undefined,
                  }));
                }}
                type="password"
                fullWidth
                size="small"
                autoComplete="new-password"
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
              />
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button size="small" onClick={cancelPasswordChange}>
                  {f({ id: "settings.button.cancel" })}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSavePassword}
                  disabled={!newPassword || changePasswordMutation.isPending}
                >
                  {f({ id: "settings.button.save" })}
                </Button>
              </Stack>
            </Stack>
          )}

          {hasAgriwebb && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.5}
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                px: 1.5,
                py: 1,
              }}
            >
              <AgriwebbLogoIcon />
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {f({ id: "settings.agriwebb" })}
              </Typography>
              <IconButton
                edge="end"
                size="small"
                aria-label={f({ id: "settings.agriwebb.remove" })}
                title={f({ id: "settings.agriwebb.remove" })}
                onClick={() => removeIntegration(ExternalProvider.AGRIWEBB)}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button
          color="inherit"
          size="small"
          startIcon={<ExitToAppIcon />}
          onClick={() => logout()}
          sx={{ color: "text.secondary" }}
        >
          {f({ id: "settings.logout" })}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>
            {f({ id: "settings.button.cancel" })}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={!profileDirty || updateProfileMutation.isPending}
          >
            {f({ id: "settings.button.save" })}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ lineHeight: 1 }}
    >
      {children}
    </Typography>
  );
}
